import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { LEAVE_POLICY_DEFAULT } from '../data/mockData';
import tokenManager from '../utils/tokenManager';
import apiService from '../services/api';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [employees,   setEmployees]   = useState([]);
  const [loadingEmps, setLoadingEmps] = useState(true);
  const [requests,    setRequests]    = useState([]);
  const [loadingReqs, setLoadingReqs] = useState(true);
  const [policy,      setPolicy]      = useState(LEAVE_POLICY_DEFAULT);
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  // ── NEW: bumped every time auth state actually changes (login, logout,
  // account switch). `loadingUser` alone isn't a reliable trigger for the
  // employees/requests effects below because it only flips false→true→false
  // once around the initial mount — if a person logs in *after* that initial
  // check already resolved (the normal SPA flow: app loads while logged out,
  // then they submit the login form), `loadingUser` never changes again and
  // those effects never re-fire, leaving `employees`/`requests` stuck at [].
  // That's what caused "works after refresh, blank right after login." ──
  const [authVersion, setAuthVersion] = useState(0);

  // ── Subscription / trial state for the whole app (banner, read-only mode).
  // { access: { state: 'full' | 'read_only', reason, trialDaysLeft }, status,
  //   trialEndsAt, currentPeriodEnd, cancelAtPeriodEnd, market, message } ──
  const [billing, setBilling] = useState(null);

  // ── Whose leave I can approve. Company admins: everyone. Department
  // admins: the people in their department(s) (and anyone who has them as
  // their named approver). { all, isDepartmentAdmin, departments, userIds } ──
  const [approvalScope, setApprovalScope] = useState(null);

  const isAdmin = ['admin', 'super_admin'].includes(currentUser?.role);

  // ── Current user — fetch FIRST, everything else depends on role ──
  useEffect(() => {
    const fetchUser = async () => {
      try {
        // Valid token → carry on. Expired token → renew it first.
        // Can't be renewed → session is ended and Layout sends to /login.
        const hasSession = await tokenManager.ensureValidSession();
        if (!hasSession) {
          setCurrentUser(null);
          return;
        }

        const response = await apiService.getMe();
        const user = response?.data?.user ?? response?.user;
        console.log(user);
        if (user) {
          const year = new Date().getFullYear();
          try {
            const allowanceRes = await apiService.getEmployeeAllowance(user.id, year);
            const allowance = allowanceRes?.data ?? allowanceRes;
            user.balance = allowance?.annualLeaveBalance ?? allowance?.balance ?? user.balance ?? 0;
            user.used    = allowance?.usedDays           ?? allowance?.used    ?? user.used    ?? 0;
          } catch (e) {
            console.warn('Could not load allowance:', e);
          }

          // ── NEW: getMe() doesn't include the employee's department — the
          // `department` column on the User model itself is unused/null,
          // and the real department lives in a join table that getMe()'s
          // query doesn't pull in. getEmployeeProfile() already resolves
          // it correctly (confirmed returning e.g. dept: "South Africa
          // Team"), so fetch it once here and merge it onto the user
          // object the same way allowance/balance are merged above. This
          // is what lets department-based UI (e.g. hiding leave balance
          // for South Africa Team) work off `currentUser.dept`. ──
          try {
            const profileRes = await apiService.getEmployeeProfile(user.id);
            const profile = profileRes?.data ?? profileRes;
            if (profile?.dept) user.dept = profile.dept;
          } catch (e) {
            console.warn('Could not load department:', e);
          }

          tokenManager.setUser(user);
          setCurrentUser(user);
        }
      } catch (error) {
        console.error('Failed to fetch current user:', error);
        // Still logged in but /auth/me failed (e.g. network) → fall back to
        // the last known user rather than rendering an empty page.
        if (tokenManager.hasSession()) {
          setCurrentUser(prev => prev ?? tokenManager.getUser() ?? null);
        }
      } finally {
        setLoadingUser(false);
      }
    };

    fetchUser();

    // ── Fired by tokenManager.setUser() (see login, refreshAccessToken,
    // switchAccount) whenever auth state changes. Previously this only
    // updated currentUser — now it also re-arms the employees/requests
    // effects so a fresh login actually populates them without a reload. ──
    const handleUserChanged = () => {
      const user = tokenManager.getUser();
      setCurrentUser(user ?? null);
      setAuthVersion(v => v + 1);

      if (user && tokenManager.hasSession()) {
        // Logged in / switched account → let the effects below re-fetch
        setLoadingEmps(true);
        setLoadingReqs(true);
      } else {
        // Logged out → clear stale company data
        setEmployees([]);
        setRequests([]);
      }
    };
    // ── Fired by tokenManager.endSession() when the session can no longer
    // be renewed. State is already cleared via userChanged; Layout handles
    // the redirect — this just tells the user why. ──
    const handleSessionExpired = () => {
      toast.error('Your session has expired. Please log in again.', { id: 'session-expired' });
    };

    window.addEventListener('userChanged', handleUserChanged);
    window.addEventListener('sessionExpired', handleSessionExpired);
    return () => {
      window.removeEventListener('userChanged', handleUserChanged);
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, []);

  // ── Employees — fetch for ALL authenticated users ─────────────
  // Everyone needs to see colleagues on the calendar and day detail.
  useEffect(() => {
    if (loadingUser) return;

    if (!tokenManager.hasSession()) {
      setLoadingEmps(false);
      return;
    }

    setLoadingEmps(true);
    apiService.getCompanyUsers()
      .then(res => {
        const list  = res?.data?.users ?? res?.users ?? res ?? [];
        const users = Array.isArray(list) ? list : [];

        setEmployees(users.map(u => ({
          ...u,
          name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email,
        })));
      })
      .catch(e => console.error('Failed to load employees:', e))
      .finally(() => setLoadingEmps(false));
  }, [loadingUser, authVersion]); // ← authVersion added

  // ── Leave requests — fetch ALL company requests for every user ─
  //
  // scope=all tells the backend to return every request in the company,
  // not just the current user's own. This is needed so that:
  //   • The calendar shows everyone's leave to all users
  //   • MiniCalendar dots reflect company-wide leave
  //   • DayDetail shows who is off on a given day
  //
  // Sensitive actions (approve, delete, view balance) remain gated
  // individually in the UI and enforced on the API.
  useEffect(() => {
    if (loadingUser) return;

    if (!tokenManager.hasSession()) {
      setLoadingReqs(false);
      return;
    }

    setLoadingReqs(true);
    apiService.getLeaveRequests({ limit: 2000, scope: 'all' })
      .then(res => {
        const list = res?.data?.requests ?? res?.requests ?? res ?? [];
        setRequests(Array.isArray(list) ? list : []);
      })
      .catch(e => console.error('Failed to load leave requests:', e))
      .finally(() => setLoadingReqs(false));
  }, [loadingUser, authVersion]); // ← authVersion added

  // ── Billing status — after sign-in, and again whenever the API says the
  // account is read only (so the banner explains why) ─────────────
  const refreshBilling = useCallback(() => {
    if (!tokenManager.hasSession()) { setBilling(null); return Promise.resolve(null); }
    return apiService.getBillingStatus()
      .then(res => { const d = res?.data ?? null; setBilling(d); return d; })
      .catch(() => null);
  }, []);

  useEffect(() => {
    if (loadingUser) return;
    refreshBilling();
  }, [loadingUser, authVersion, refreshBilling]);

  useEffect(() => {
    const onReadOnly = (e) => {
      toast.error(e.detail?.message || 'Your account is read only at the moment.', { id: 'subscription-read-only', duration: 6000 });
      refreshBilling();
    };
    window.addEventListener('subscriptionReadOnly', onReadOnly);
    return () => window.removeEventListener('subscriptionReadOnly', onReadOnly);
  }, [refreshBilling]);

  const isReadOnly = billing?.access?.state === 'read_only' && currentUser?.role !== 'super_admin';

  useEffect(() => {
    if (loadingUser) return;
    if (!tokenManager.hasSession()) { setApprovalScope(null); return; }
    apiService.getApprovalScope()
      .then(res => setApprovalScope(res?.data ?? null))
      .catch(() => setApprovalScope(null));
  }, [loadingUser, authVersion]);

  const approverIds = new Set(approvalScope?.userIds ?? []);
  const isDepartmentAdmin = !!approvalScope?.isDepartmentAdmin;
  // Can I approve / reject this person's leave? (Never my own unless I'm a company admin.)
  const canApprove = (empId) => isAdmin || (!!empId && empId !== currentUser?.id && approverIds.has(empId));

  // ── Mutations ─────────────────────────────────────────────────
  const addRequest = (newReq) => {
    if (!newReq?.id) return;
    setRequests(prev => {
      if (prev.some(r => r.id === newReq.id)) return prev;
      return [newReq, ...prev];
    });
  };

  const updateStatus   = (id, status) =>
    setRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r));

  const deleteRequest  = (id) =>
    setRequests(prev => prev.filter(r => r.id !== id));

  const updateEmployee = (updated) =>
    setEmployees(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));

  // ── Derived ───────────────────────────────────────────────────
  const getEmployee    = (id) => employees.find(e => e.id === id);
  const getRequestsFor = (empId) => requests.filter(r => r.empId === empId);
  const pendingCount   = requests.filter(r => r.status === 'pending' && canApprove(r.empId)).length;
  const updatePolicy   = (updates) => setPolicy(prev => ({ ...prev, ...updates }));

  return (
    <AppContext.Provider value={{
      employees, loadingEmps,
      requests,  loadingReqs,
      policy, currentUser, loadingUser, authVersion,
      isAdmin,
      billing, isReadOnly, refreshBilling,
      approvalScope, isDepartmentAdmin, canApprove,
      addRequest, updateStatus, deleteRequest,
      getEmployee, getRequestsFor, pendingCount,
      updatePolicy, updateEmployee,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be inside AppProvider');
  return ctx;
};